import { httpClient } from './http-client';
import { normalizeEmail } from '@/lib/utils/normalize-email';
import { getAttributionSnapshot } from '@/lib/utils/attribution';

export interface AuthorizationUrlResponse {
  authorizationUrl: string;
}

export const authApi = {
  checkEmail: async (email: string): Promise<{ available: boolean }> => {
    const response = await httpClient.post('/auth/check-email', { email });
    return response.data;
  },

  getInviteInfo: async (token: string): Promise<{ email: string; organizationName: string; expired: boolean }> => {
    const response = await httpClient.get(`/auth/invite-info/${encodeURIComponent(token)}`);
    return response.data;
  },

  login: async (email: string, password: string) => {
    const response = await httpClient.post('/auth/login', {
      email: normalizeEmail(email),
      password,
    });
    return response.data;
  },

  register: async (data: {
    email: string;
    password: string;
    name: string;
    promoCode?: string;
    inviteToken?: string;
    tosAcceptedAt: string;
    referralCode?: string;
    referralSource?: string;
  }) => {
    const response = await httpClient.post('/auth/register', {
      ...data,
      ...getAttributionSnapshot(),
    });
    return response.data;
  },

  verifyEmail: async (email: string, code: string) => {
    const response = await httpClient.post('/auth/verify-email', { email, code });
    return response.data;
  },

  resendVerification: async (email: string) => {
    const response = await httpClient.post('/auth/resend-verification', { email });
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await httpClient.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string) => {
    const response = await httpClient.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  validateAccessCode: async (code: string): Promise<{ valid: boolean }> => {
    const response = await httpClient.post('/auth/validate-access-code', { code });
    return response.data;
  },

  validateReferralCode: async (referralCode: string): Promise<{ valid: boolean }> => {
    const response = await httpClient.post('/referrals/validate', { referralCode });
    return response.data;
  },

  /**
   * Server-side cookie clear only. Use `useAuth().logout()` for full logout
   * (state reset, cross-tab broadcast, navigation).
   * @internal Called by AuthProvider — do not use directly.
   */
  _serverLogout: async () => {
    await httpClient.post('/auth/logout');
  },

  getProfile: async () => {
    const response = await httpClient.get('/auth/profile');
    return response.data;
  },

  completeOauthRegistration: async (data: {
    token: string;
    tosAcceptedAt: string;
    email?: string;
    promoCode?: string;
    referralCode?: string;
    referralSource?: string;
  }) => {
    const response = await httpClient.post('/auth/oauth/complete-registration', {
      ...data,
      ...getAttributionSnapshot(),
    });
    return response.data;
  },

  getGoogleAuthUrl: async (): Promise<AuthorizationUrlResponse> => {
    const response = await httpClient.get('/auth/google/authorize');
    return response.data;
  },

  getLinkedinAuthUrl: async (): Promise<AuthorizationUrlResponse> => {
    const response = await httpClient.get('/auth/linkedin/authorize');
    return response.data;
  },

  getAzureAuthUrl: async (): Promise<AuthorizationUrlResponse> => {
    const response = await httpClient.get('/auth/azure/authorize');
    return response.data;
  },
};
